import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { produccionApi, errorMessage } from '../utils/produccionApi';
import { formatKg, formatDate, formatDateTime, STAGE_LABELS } from '../utils/produccion';
import '../styles/produccion.css';

const LINE_STATUS = { pendiente: 'Pendiente', completa: 'Completa', corta: 'Corta' };
const EVENT_LABELS = {
  creada: 'Orden creada',
  plan_editado: 'Plan editado',
  retiro: 'Retiro',
  no_esta: 'Lote no encontrado',
  linea_corta: 'Línea cerrada corta',
  terminada: 'Producción terminada',
  cerrada: 'Producción cerrada',
  valor_editado: 'Valor corregido al cerrar',
  retiro_liberado: 'Retiro liberado',
  anulada: 'Orden anulada',
};
const REASON_LABELS = {
  no_esta: 'no estaba', danado: 'dañado', otro: 'otro motivo', sin_stock: 'sin stock', operario: 'terminada por el operario',
};
const STAGE_CHIP = { anulada: 'red', antigua: 'amber' };

const kg = (x) => `${formatKg(Number(x))} kg`;
const when = formatDateTime;
const place = (rack, shelf) => (rack ? `${rack}-${shelf}` : '');
const expiry = (date) => (date ? `vence ${formatDate(date)}` : 'sin vencimiento');
const skuLabel = (sku) => (sku ? sku.name : '');
const mermaText = (t) => `${kg(t.merma_kg)} · ${formatKg(Number(t.merma_pct))} %`;
// Every planned product; orders from before the list show their single product.
const productNames = (order) => {
  const products = order.products && order.products.length ? order.products : [order.product].filter(Boolean);
  return products.map((p) => p.name).join(', ');
};

function Section({ title, children }) {
  return (
    <section className="prod-sec">
      <div className="prod-lbl">{title}</div>
      {children}
    </section>
  );
}

function ProduccionDetalle() {
  const { token, userRole } = useAuth();
  const navigate = useNavigate();
  const { po_number: poNumber } = useParams();
  const [searchParams] = useSearchParams();
  const orderPath = `orders/${encodeURIComponent(poNumber)}`;

  const [order, setOrder] = useState(null);
  const [loadError, setLoadError] = useState('');
  const [cancelling, setCancelling] = useState(false);
  const [reason, setReason] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [releasing, setReleasing] = useState(false);
  const busyRef = useRef(false);
  const mounted = useRef(true);

  const load = useCallback(async () => {
    const res = await produccionApi.get(`${orderPath}/`, token);
    if (!mounted.current) return;
    if (res.ok) setOrder(res.data);
    else setLoadError(errorMessage(res));
  }, [orderPath, token]);

  useEffect(() => {
    mounted.current = true;
    load();
    return () => { mounted.current = false; };
  }, [load]);

  // Frees an order someone left half retired; what they saved stays.
  const release = async () => {
    if (busyRef.current) return;
    busyRef.current = true;
    setBusy(true);
    setError('');
    const res = await produccionApi.send('POST', `${orderPath}/retiro/release/`, {}, token);
    busyRef.current = false;
    setBusy(false);
    if (!res.ok) {
      setError(errorMessage(res));
      return;
    }
    setReleasing(false);
    await load();
  };

  const cancel = async () => {
    if (busyRef.current || !reason.trim()) return;
    busyRef.current = true;
    setBusy(true);
    setError('');
    const res = await produccionApi.send('POST', `${orderPath}/cancel/`, { reason: reason.trim() }, token);
    busyRef.current = false;
    setBusy(false);
    if (res.ok) {
      setOrder(res.data);
      setCancelling(false);
      setReason('');
      return;
    }
    setError(errorMessage(res));
  };

  const header = (
    <div className="prod-retiro-head">
      <div>
        <h1 className="prod-title">{poNumber}</h1>
        {order && productNames(order) && <div className="prod-sub" style={{ margin: 0 }}>{productNames(order)}</div>}
      </div>
      <button type="button" className="prod-btn ghost prod-btn-small prod-noprint" onClick={() => navigate('/production')}>← Volver</button>
    </div>
  );

  if (loadError) {
    return (
      <div className="prod-page">
        {header}
        <div className="prod-bad prod-error">{loadError}</div>
      </div>
    );
  }
  if (!order) {
    return (
      <div className="prod-page">
        {header}
        <div className="prod-sub">Cargando…</div>
      </div>
    );
  }

  const { stage } = order;
  const go = (suffix) => navigate(`/production/${poNumber}${suffix}`);
  // Robin's production_closer account only looks at orders and closes finished ones.
  const closer = userRole === 'production_closer';
  const canCancel = !closer && ['por_retirar', 'retirando', 'lista_para_terminar', 'antigua'].includes(stage)
    && ['pending', 'in_process'].includes(order.status);
  const lines = order.lines || [];
  const pulls = order.pulls || [];
  const outputs = order.outputs || [];
  const supplies = order.supplies || [];
  const events = order.events || [];
  const alerts = events.filter((e) => e.kind === 'no_esta' || e.kind === 'linea_corta');
  const tr = order.transformation;
  const closed = Boolean(tr && ['finished', 'completed'].includes(order.status) && !order.legacy);
  const fixed = tr && tr.corrected; // Robin's figures at Cerrar, if any
  const holder = ['por_retirar', 'retirando'].includes(stage) ? order.retiro_holder : null;
  const counts = [['Bolsa', order.bolsa], ['Interfolio', order.interfolio], ['Etiqueta', order.etiqueta]]
    .filter(([, value]) => value !== null && value !== undefined);
  const balance = tr && !order.legacy ? (tr.balance || []) : [];
  const open = order.status === 'pending' || order.status === 'in_process';
  const params = lines.filter((l) => l.temperature || l.thickness || l.weight_reference);
  // Legacy orders moved stock outside this flow: their lines only hold the plan.
  const legacy = order.legacy || stage === 'antigua';

  const who = [
    `Creada por ${order.created_by || '—'} · ${when(order.created_at)}`,
    order.finished_at ? `Terminada por ${order.finished_by || '—'} · ${when(order.finished_at)}` : '',
    order.completed_at ? `Cerrada por ${order.closed_by || '—'} · ${when(order.completed_at)}` : '',
    order.cancelled_at ? `Anulada por ${order.cancelled_by || '—'} · ${when(order.cancelled_at)}` : '',
  ].filter(Boolean);

  return (
    <div className="prod-page">
      {['terminada', 'cerrada'].filter((key) => searchParams.get(key) === '1').map((key) => (
        <div className="prod-done-hero" key={key}>
          <div className="ck">✓</div>
          <div className="msg">{key === 'terminada' ? 'Producción terminada' : 'Producción cerrada'}</div>
        </div>
      ))}
      {header}
      <div className="prod-card">
        <span className={`prod-chip ${STAGE_CHIP[stage] || ''}`}>{STAGE_LABELS[stage] || stage}</span>
        {who.map((text) => <div className="d" key={text}>{text}</div>)}
        {holder && <div className="d">{`Retirando: ${holder}`}</div>}
        {counts.length > 0 && <div className="d">{counts.map(([label, value]) => `${label} ${value}`).join(' · ')}</div>}
        {order.cancel_reason && <div className="d">{`Motivo: ${order.cancel_reason}`}</div>}
        {order.no_output_reason && <div className="d">{`No salió producto: ${order.no_output_reason}`}</div>}
      </div>
      {order.legacy && stage !== 'antigua' && <div className="prod-sub">Orden antigua</div>}
      {order.notes && <div className="prod-note">{order.notes}</div>}
      {order.close_notes && <div className="prod-note">{order.close_notes}</div>}

      <div className="prod-noprint">
        {stage === 'por_retirar' && !closer && (
          <>
            <button type="button" className="prod-btn green" onClick={() => go('/retiro')}>Retirar</button>
            <button type="button" className="prod-btn ghost" onClick={() => go('/editar')}>Editar</button>
          </>
        )}
        {stage === 'retirando' && !closer && (
          <button type="button" className="prod-btn green" onClick={() => go('/retiro')}>Seguir retiro</button>
        )}
        {stage === 'lista_para_terminar' && !closer && (
          <button type="button" className="prod-btn green" onClick={() => go('/terminar')}>Terminar</button>
        )}
        {stage === 'terminada' && (userRole === 'admin' || closer
          ? <button type="button" className="prod-btn green" onClick={() => go('/cerrar')}>Cerrar</button>
          : <div className="prod-sub">Esperando cierre de un administrador</div>)}
        {holder && !closer && !releasing && (
          <button type="button" className="prod-btn ghost" onClick={() => setReleasing(true)}>Liberar</button>
        )}
        {holder && releasing && (
          <div className="prod-panel">
            <div className="prod-panel-q">
              {`${holder} la está retirando. Lo que tenga escaneado sin guardar se pierde y tiene que volver al rack o escanearlo quien siga.`}
            </div>
            {error && <div className="prod-bad prod-error">{error}</div>}
            <div className="prod-submit-row">
              <button type="button" className="prod-btn red" disabled={busy} onClick={release}>Sí, liberar</button>
              <button type="button" className="prod-btn ghost" disabled={busy} onClick={() => { setReleasing(false); setError(''); }}>Volver</button>
            </div>
          </div>
        )}
        {canCancel && !cancelling && (
          <button type="button" className="prod-btn red" onClick={() => setCancelling(true)}>Anular</button>
        )}
        <button type="button" className="prod-btn ghost" onClick={() => window.print()}>Imprimir</button>
        {cancelling && (
          <div className="prod-panel">
            <div className="prod-panel-q">¿Por qué se anula? Lo retirado vuelve a su lote.</div>
            <textarea
              className="prod-input"
              rows={2}
              aria-label="Motivo de la anulación"
              value={reason}
              disabled={busy}
              onChange={(e) => setReason(e.target.value)}
            />
            {error && <div className="prod-bad prod-error">{error}</div>}
            <div className="prod-submit-row">
              <button type="button" className="prod-btn red" disabled={!reason.trim() || busy} onClick={cancel}>
                {busy ? 'Anulando…' : 'Confirmar anulación'}
              </button>
              <button
                type="button"
                className="prod-btn ghost"
                disabled={busy}
                onClick={() => { setCancelling(false); setError(''); }}
              >
                Volver
              </button>
            </div>
          </div>
        )}
      </div>

      {outputs.length > 0 && (
        <Section title="Producto terminado">
          {outputs.map((o) => (
            <div className="prod-card green" key={`${o.sku.id}|${o.batch}`}>
              <div className="n">{o.sku.name}</div>
              <div className="prod-batch">{o.batch}</div>
              <div className="d">
                {`${expiry(o.expiration_date)} · ${place(o.rack, o.shelf)} · ${formatKg(Number(o.quantity))} ${o.sku.stock_unit === 'un' ? 'un.' : 'kg'}`}
                {o.weight ? ` · ${kg(o.weight)}` : ''}
              </div>
            </div>
          ))}
        </Section>
      )}

      {lines.length > 0 && (
        <Section title={legacy ? 'Plan' : 'Plan vs real'}>
          {lines.map((l) => (
            <div className="prod-rline" key={l.id}>
              <span>{l.sku.name}</span>
              {legacy ? <span>{kg(l.planned)}</span> : (
                <>
                  <span>{`${kg(l.pulled)} de ${kg(l.planned)}`}</span>
                  <span className={`prod-chip ${l.status === 'corta' ? 'amber' : ''}`}>{LINE_STATUS[l.status] || l.status}</span>
                </>
              )}
            </div>
          ))}
          {params.map((l) => (
            <div className="prod-note" key={`p${l.id}`}>
              {[
                l.sku.name,
                // Same units as the plan form: Espesor (mm), Peso feta (g).
                l.temperature ? `temperatura ${formatKg(Number(l.temperature))} °C` : '',
                l.thickness ? `espesor ${formatKg(Number(l.thickness))} mm` : '',
                l.weight_reference ? `peso feta ${formatKg(Number(l.weight_reference))} g` : '',
              ].filter(Boolean).join(' · ')}
            </div>
          ))}
        </Section>
      )}

      {pulls.length > 0 && (
        <Section title="Retiros">
          <div className="prod-trace">
            {pulls.map((p) => (
              <div className="row" key={p.id}>
                {`${p.sku.name} · lote ${p.batch} (${expiry(p.expiration_date)}) · ${place(p.rack, p.shelf)} · ${kg(p.quantity)}`
                  + `${p.pieces && p.pieces.length ? ` · ${p.pieces.length} pzas` : ''} · ${p.user} · ${when(p.created_at)}`}
              </div>
            ))}
          </div>
        </Section>
      )}

      {alerts.length > 0 && (
        <Section title="Alertas">
          {alerts.map((e, i) => (
            <div className="prod-card amber" key={`${e.kind}${e.created_at}${i}`}>
              <div className="n">{`${EVENT_LABELS[e.kind]}${e.reason ? ` (${REASON_LABELS[e.reason] || e.reason})` : ''}`}</div>
              <div className="d">
                {[skuLabel(e.sku), e.batch && `lote ${e.batch}`, place(e.rack, e.shelf), e.user, when(e.created_at), e.note]
                  .filter(Boolean).join(' · ')}
              </div>
            </div>
          ))}
        </Section>
      )}

      {supplies.length > 0 && (
        <Section title="Insumos">
          <div className="prod-trace">
            {supplies.map((s) => (
              <div className="row" key={`${s.sku.id}|${s.batch}`}>
                {`${s.sku.name} · lote ${s.batch} (${expiry(s.expiration_date)}) · ${place(s.rack, s.shelf)} · ${formatKg(Number(s.quantity))}`}
              </div>
            ))}
          </div>
        </Section>
      )}

      {balance.length > 0 && (
        <Section title="Transformación">
          {closed && (
          <div className="prod-sum">
            <div className="r"><span>Entró</span><b>{fixed ? `${kg(fixed.raw_used_kg)} (Terminar: ${kg(tr.raw_used_kg)})` : kg(tr.raw_used_kg)}</b></div>
            <div className="r"><span>Salió</span><b>{kg(tr.output_kg)}</b></div>
            <div className="r"><span>Merma</span><b>{fixed
              ? `${mermaText(fixed)} (Terminar: ${mermaText(tr)})` : mermaText(tr)}</b></div>
            <div className="r"><span>Devuelto</span><b>{fixed ? `${kg(fixed.returned_kg)} (Terminar: ${kg(tr.returned_kg)})` : kg(tr.returned_kg)}</b></div>
            {fixed && <div className="r"><span className="prod-sub" style={{ margin: 0 }}>Corregido al cerrar: no movió stock.</span></div>}
          </div>
          )}
          <div className="prod-table-wrap">
            <table className="prod-table">
              <thead>
                <tr>
                  <th>Producto</th><th>Lote</th><th>Retirado</th><th>Devuelto</th><th>Usado</th><th>Merma</th><th>Saldo</th><th />
                </tr>
              </thead>
              <tbody>
                {tr.balance.map((b) => {
                  const ok = Number(b.saldo) === 0;
                  const pending = !ok && open;
                  return (
                    <tr key={`${b.sku_code}|${b.batch}`}>
                      <td>{b.sku_code}</td>
                      <td>{b.batch}</td>
                      <td>{formatKg(Number(b.pulled))}</td>
                      {['returned', 'consumed', 'merma'].map((key) => (
                        <td key={key}>
                          {formatKg(Number(b.corrected ? b.corrected[key] : b[key]))}
                          {b.corrected && Number(b.corrected[key]) !== Number(b[key]) && (
                            <div className="prod-sub" style={{ margin: 0 }}>{`antes ${formatKg(Number(b[key]))}`}</div>
                          )}
                        </td>
                      ))}
                      <td>{formatKg(Number(b.saldo))}</td>
                      <td className={ok ? 'prod-ok' : (pending ? 'prod-warn' : 'prod-bad')}>
                        {ok ? '✓' : (pending ? 'en producción' : '✗')}
                        {b.corrected && <div><span className="prod-chip amber">corregido al cerrar</span></div>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Section>
      )}

      {events.length > 0 && (
        <Section title="Historial">
          <div className="prod-trace">
            {events.map((e, i) => (
              <div className="row" key={`${e.kind}${e.created_at}${i}`}>
                {[
                  `${when(e.created_at)} · ${EVENT_LABELS[e.kind] || e.kind}`,
                  skuLabel(e.sku),
                  e.batch && `lote ${e.batch}`,
                  e.kind === 'valor_editado' && `${e.field} ${formatKg(Number(e.old_value))} → ${formatKg(Number(e.new_value))}`,
                  e.quantity && kg(e.quantity),
                  e.reason && (REASON_LABELS[e.reason] || e.reason),
                  e.note,
                  e.user,
                ].filter(Boolean).join(' · ')}
              </div>
            ))}
          </div>
        </Section>
      )}
    </div>
  );
}

export default ProduccionDetalle;
